import { useState } from 'react'
import { Check, Copy, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import { cn } from '@/lib/utils'

interface MessageActionsProps {
  content: string
  className?: string
  onRegenerate?: () => void
}

/**
 * Hover actions for assistant text messages.
 * Shows "Copy" and "Regenerate" buttons.
 * Per-section "Apply" is handled by ApplyableResponse instead.
 */
export function MessageActions({ content, className, onRegenerate }: MessageActionsProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Failed to copy')
    }
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          'flex items-center gap-0.5 pt-1 transition-opacity',
          className
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-placeholder transition-colors hover:bg-control-hover-strong hover:text-text-secondary cursor-pointer"
              aria-label="Copy response"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{copied ? 'Copied' : 'Copy'}</TooltipContent>
        </Tooltip>
        {onRegenerate && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onRegenerate}
                className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-placeholder transition-colors hover:bg-control-hover-strong hover:text-text-secondary cursor-pointer"
                aria-label="Retry response"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Retry</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  )
}
